import React, { useEffect, useRef, useState } from 'react';
import { IconButton, TextField, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { extractApiErrorMessage } from 'services/callScribe/apiError';
import {
  SPEAKER_NAME_ERROR,
  renameCallScribeParticipant,
  validateSpeakerName,
} from 'services/callScribe/renameParticipant';

export interface IRenamedSpeaker {
  label: string;
  nameSource: string | number;
}

export interface IRenameSpeakerProps {
  callId: number;
  participantId: number;
  describedById?: string;
  onRenamed: (renamed: IRenamedSpeaker) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  pencil: {
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    color: alpha(theme.palette.text.primary, 0.64),
    transition: 'background-color 120ms ease-out, color 120ms ease-out',
    '&:hover': { color: theme.palette.text.primary, backgroundColor: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
  },
  errorText: { color: theme.palette.error.main },
  field: { minWidth: 180, flex: '1 1 180px' },
  actions: { display: 'flex', gap: theme.spacing(0.5), paddingTop: theme.spacing(0.5) },
  action: {
    ...theme.typography.caption,
    fontWeight: 600,
    minHeight: 32,
    padding: theme.spacing(0, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transition: 'background-color 120ms ease-out',
    '&:hover:not(:disabled)': { backgroundColor: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  actionPrimary: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const RenameSpeaker: React.FC<IRenameSpeakerProps> = ({
  callId,
  participantId,
  describedById,
  onRenamed,
}) => {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pencilRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef(false);
  const errorId = `rename-speaker-error-${participantId}`;

  useEffect(() => {
    if (!editing && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      pencilRef.current?.focus();
    }
  }, [editing]);

  const cancel = (): void => {
    restoreFocusRef.current = true;
    setEditing(false);
    setError(null);
    setValue('');
  };

  const save = async (): Promise<void> => {
    const invalid = validateSpeakerName(value);
    if (invalid) {
      setError(invalid);
      return;
    }
    const name = value.trim();
    setSaving(true);
    setError(null);
    try {
      const res = await renameCallScribeParticipant(callId, participantId, name);
      setSaving(false);
      setEditing(false);
      setValue('');
      onRenamed({
        label: res?.data?.label?.trim() || name,
        nameSource: res?.data?.nameSource ?? 'Manual',
      });
    } catch (err) {
      setSaving(false);
      setError(extractApiErrorMessage(err, SPEAKER_NAME_ERROR));
    }
  };

  if (!editing) {
    return (
      <IconButton
        ref={pencilRef}
        size="small"
        className={classes.pencil}
        aria-label="Name this speaker"
        aria-describedby={describedById}
        onClick={() => { setEditing(true); }}
      >
        <EditOutlinedIcon fontSize="inherit" />
      </IconButton>
    );
  }

  return (
    <form
      className={classes.form}
      onSubmit={e => { e.preventDefault(); void save(); }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } }}
    >
      <TextField
        autoFocus
        size="small"
        label="Speaker name"
        className={classes.field}
        value={value}
        onChange={e => { setValue(e.target.value); if (error) setError(null); }}
        error={Boolean(error)}
        inputProps={{ maxLength: 200, 'aria-describedby': error ? errorId : undefined, 'aria-invalid': Boolean(error) }}
        disabled={saving}
      />
      <span className={classes.actions}>
        <button type="submit" className={`${classes.action} ${classes.actionPrimary}`} disabled={saving}>
          Save
        </button>
        <button type="button" className={classes.action} onClick={cancel} disabled={saving}>
          Cancel
        </button>
      </span>
      <div id={errorId} role="alert" className={classes.errorText} style={{ flexBasis: '100%', fontSize: 12 }}>
        {error}
      </div>
    </form>
  );
};

export default RenameSpeaker;
